import React, { FunctionComponent, useCallback, useState } from "react";
import Link from "next/link";
import NextImage from "next/image";
import { Navbar, Nav, Row, Col, Container, Offcanvas, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faFacebook, faInstagram } from "@fortawesome/free-brands-svg-icons";

import { faHome, faUsers, faHotel, faIdCard, faConciergeBell, faQuestionCircle, faBlog, faNewspaper, faPen, IconDefinition } from "@fortawesome/free-solid-svg-icons";

import { css, cx } from "@adjust/libs/emotion";

import NavHeaderLinkComponent from "./NavHeaderLinkComponent";

import NavCanvasLinkComponent from "./NavCanvasLinkComponent";
import NavCanvasSocialComponent from "./NavCanvasSocialComponent";

import { FACEBOOK_LINK, INSTAGRAM_LINK } from "libs/constants";

import HEADER_LOGO_HORIZONTAL from "@adjust/public/images/logo/Logo Design - Horizontal.svg";
import HEADER_LOGO_VERTICAL from "@adjust/public/images/logo/Logo Design - Vertical.svg";

const ffLato = css({
  fontWeight: 900,
  fontFamily: "Lato, sans-serif",
});

const topHeader = css({
  background: "#fff",
  // background: "#0c1730",
  padding: "28px 24px",
});

const logo = css({
  display: "inline-block",

  "& img": {
    width: "336px",
    maxWidth: "100%",
    height: "auto",
  },
});

const navbar = css({
  background: "#fff",
  // borderTop: "1px solid #eef3fb",
  borderBottom: "1px solid #d7e7ff",
  minHeight: "62px",
});

const header = css({
  display: "contents",
});

const ocTitle = css({
  fontFamily: "Lato, sans-serif",
  fontSize: "1.5rem",
});

const socialCard = css({
  display: "flex",
  alignItems: "center",
  gap: "10px",

  padding: "10px 18px",
  marginBottom: "10px",

  border: "1px solid #d7e7ff",
  borderRadius: "8px",

  backgroundColor: "#fff",

  cursor: "pointer",
  transition: "all .2s ease",

  "&:hover": {
    backgroundColor: "#f5f9ff",
    borderColor: "#8cb8ff",
  },
});

const socialLabel = css({
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: ".95rem",
  color: "#1f355e",
});

const tagline = css({
  maxWidth: "368px",
  paddingLeft: "24px",
});

const taglineTitle = css({
  fontFamily: "Lato, sans-serif",
  fontSize: "1.25rem",
  fontWeight: 800,
  color: "#1f5fbf",
  lineHeight: 1.2,
  marginBottom: "0.35rem",
});

const taglineSubtitle = css({
  fontFamily: "Lato, sans-serif",
  fontSize: "0.95rem",
  color: "#5b6675",
  lineHeight: 1.5,
});

type NavLinkProps = {
  name: string;
  icon: IconDefinition;
  href: string;
};

const navigation: NavLinkProps[] = [
  {
    name: "Home",
    icon: faHome,
    href: "/",
  },
  {
    name: "Meet the Gray's",
    icon: faUsers,
    href: "/about/",
  },
  {
    name: "Contact",
    icon: faIdCard,
    href: "/contact/",
  },
  // {
  //   name: "Destinations",
  //   icon: faPlane,
  //   href: "/destinations/",
  // },
  {
    name: "Testimonials",
    icon: faPen,
    href: "/testimonials/",
  },
  {
    name: "Sandals / Beaches",
    icon: faHotel,
    href: "/sandals-beaches-resorts/",
  },
  {
    name: "Services",
    icon: faConciergeBell,
    href: "/services/",
  },
  {
    name: "FAQ",
    icon: faQuestionCircle,
    href: "/faq/",
  },
  // {
  //   name: "Blog",
  //   icon: faBlog,
  //   href: "/blogs/",
  // },
  {
    name: "Press",
    icon: faNewspaper,
    href: "/press/",
  },
];

type NavSocialProps = {
  name: string;
  icon: IconDefinition;
  color: string;
  href: string;
};

type SocialsType = "Facebook" | "Instagram";

const socials: { [key in SocialsType]: NavSocialProps } = {
  Facebook: {
    name: "Facebook",
    icon: faFacebook,
    color: "#4267B2",
    href: FACEBOOK_LINK,
  },
  Instagram: {
    name: "Instagram",
    icon: faInstagram,
    color: "#DD2A7B",
    href: INSTAGRAM_LINK,
  },
};

const HeaderComponent: FunctionComponent = () => {
  const [show, setShow] = useState(false);

  const handleShow = useCallback(() => setShow(true), []);
  const handleClose = useCallback(() => setShow(false), []);

  return (
    <header id="header" className={header} itemType="https://schema.org/WPHeader" itemScope>
      {/* Desktop Header */}

      <Container fluid className={cx(topHeader, "d-none d-lg-block")}>
        <Row className="align-items-center">
          <Col lg={4} className="d-flex align-items-center">
            <div className={tagline}>
              <div className={taglineTitle}>Curated Travel. Elevated Experiences.</div>

              <div className={taglineSubtitle}>Creating Extraordinary Journeys, One Personalized Experience at a Time.</div>
            </div>
          </Col>

          <Col lg={4} className="text-center">
            <Link href="/" className={logo}>
              <NextImage src={HEADER_LOGO_VERTICAL} alt="Adjust to the Good Life Travel" priority unoptimized />
            </Link>
          </Col>

          <Col lg={4} className="d-flex flex-column align-items-end justify-content-center">
            {[socials.Facebook, socials.Instagram].map((social) => (
              <Nav.Link key={social.name} className="d-flex" rel="noopener nofollow" target="_blank" href={social.href}>
                <div className={socialCard}>
                  <FontAwesomeIcon icon={social.icon} color={social.color} size="2x" />
                  <span className={socialLabel}>{social.name}</span>
                </div>
              </Nav.Link>
            ))}
          </Col>
        </Row>
      </Container>

      {/* Navbar */}

      <Navbar expand="lg" sticky="top" className={navbar}>
        <Container fluid>
          {/* Mobile */}

          <Link href="/" className="d-lg-none me-2">
            <NextImage src={HEADER_LOGO_HORIZONTAL} alt="Logo" width={232} height={65.5} priority unoptimized />
          </Link>

          <Navbar.Toggle className="ms-auto py-2" onClick={handleShow} />

          <Navbar.Collapse className="d-none d-lg-flex justify-content-center">
            <Nav className={cx(ffLato, "mx-auto")}>
              {navigation.map((nav) => (
                <NavHeaderLinkComponent key={nav.name} {...nav} />
              ))}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Mobile Offcanvas */}

      <Offcanvas show={show} onHide={handleClose} placement="end">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className={ocTitle}>Adjust to the Good Life Travel</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <Container fluid>
            <Row className={cx(ffLato, "justify-content-center")}>
              {navigation.map((nav) => (
                <NavCanvasLinkComponent key={nav.name} {...nav} />
              ))}
            </Row>

            <hr />

            <Row className={cx(ffLato, "justify-content-center")}>
              <NavCanvasSocialComponent {...socials.Facebook} />
              <NavCanvasSocialComponent {...socials.Instagram} />
            </Row>
          </Container>
        </Offcanvas.Body>
      </Offcanvas>
    </header>
  );
};

export default React.memo(HeaderComponent);
